<template>
  <!-- Main container for the template creation/editing UI -->
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body">
              <!-- Header: Title and Cancel button -->
              <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <div>
                  <h1 class="h4 mb-1">
                    <!-- Display different title based on create vs edit mode -->
                    {{ isEdit ? "Edit Template" : "New Template" }}
                  </h1>
                  <p class="help mb-0">
                    Build a refurbish template by selecting steps and arranging them in order.
                  </p>
                </div>

                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  @click="handleCancel"
                >
                  Cancel
                </button>
              </div>

              <!-- Loading spinner shown while fetching initial data -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status"></div>
              </div>

              <!-- Main form (shown when not loading) -->
              <div v-else>
                <form @submit.prevent="handleSubmit">
                  <!-- Template metadata fields: name and active status -->
                  <div class="row g-3 mb-3">
                    <div class="col-md-6">
                      <label class="form-label">Template Name</label>
                      <input
                        v-model="form.template_name"
                        type="text"
                        class="form-control"
                        required
                      />
                    </div>
                    <div class="col-md-6 d-flex align-items-end">
                      <div class="form-check mt-2">
                        <input
                          id="activeTemplate"
                          v-model="form.active_template"
                          class="form-check-input"
                          type="checkbox"
                        />
                        <label class="form-check-label" for="activeTemplate">
                          Active template
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Template description field for explaining the template's purpose -->
                  <div class="mb-3">
                    <label class="form-label">Description</label>
                    <textarea
                      v-model="form.template_description"
                      class="form-control"
                      rows="2"
                      placeholder="Explain when to use this template and any key notes."
                    ></textarea>
                  </div>

                  <!-- Two-column layout: Step library (left) and Template steps (right) -->
                  <div class="row g-3">
                    <!-- Left column: Step library with search and new step creation -->
                    <div class="col-md-5">
                      <div class="border rounded p-3 h-100 bg-white">
                        <div class="d-flex justify-content-between mb-2">
                          <h2 class="h6 mb-0">Step Library</h2>
                          <span class="help">
                            Click a step to add it to this template.
                          </span>
                        </div>

                        <!-- Section for creating a brand new step -->
                        <div class="border rounded p-2 mb-3 bg-light">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <strong class="small mb-0">New Step</strong>
                          </div>

                          <!-- Step name input -->
                          <div class="mb-2">
                            <input
                              v-model="newStep.step_name"
                              type="text"
                              class="form-control form-control-sm"
                              placeholder="Step name"
                            />
                          </div>

                          <!-- Step description input -->
                          <div class="mb-2">
                            <textarea 
                              v-model="newStep.step_description" 
                              class="form-control form-control-sm" 
                              rows="2"
                              placeholder="Step description"
                            ></textarea>
                          </div>

                          <!-- Step category dropdown -->
                          <div class="mb-2">
                            <label class="form-label small mb-1">Category</label>
                            <select v-model="newStep.step_category" class="form-select form-select-sm">
                              <option disabled value="">Select category...</option>
                              <option 
                                v-for="opt in stepCategoryOptions" 
                                :key="opt.value" 
                                :value="opt.value"
                              >
                                {{ opt.label }}
                              </option>
                            </select>
                          </div>

                          <!-- Add Step button: disabled until all required fields are filled -->
                          <div class="d-flex justify-content-end">
                            <button
                              type="button"
                              class="btn btn-primary btn-sm"
                              :disabled="
                                creatingStep ||
                                !newStep.step_name.trim() ||
                                !newStep.step_category
                              "
                              @click="handleCreateStep"
                            >
                              <!-- Show spinner while creating step -->
                              <span
                                v-if="creatingStep"
                                class="spinner-border spinner-border-sm me-1"
                              ></span>
                              Add Step
                            </button>
                          </div>
                        </div>

                        <!-- Search input to filter steps in the library -->
                        <div class="mb-2">
                          <input 
                            v-model="stepSearch" 
                            type="text" 
                            class="form-control form-control-sm"
                            placeholder="Search steps..."
                          />
                        </div>

                        <!-- Empty state when no steps are available -->
                        <div
                          v-if="!Array.isArray(steps) || steps.length === 0"
                          class="text-muted small"
                        >
                          No steps available.
                        </div>

                        <!-- Scrollable list of steps from the library -->
                        <div
                          v-else
                          class="list-group small"
                          style="max-height: 320px; overflow-y: auto;"
                        >
                          <!-- Loop through filtered steps -->
                          <div
                            v-for="step in filteredSteps"
                            :key="step.step_id"
                            class="list-group-item"
                          >
                            <!-- Normal (collapsed) view when not editing this step -->
                            <div
                              v-if="editingStepId !== step.step_id"
                              class="d-flex justify-content-between align-items-start"
                            >
                              <!-- Step details: name, description, category -->
                              <div class="me-2">
                                <div class="fw-semibold">
                                  {{ step.step_name }}
                                </div>
                                <div class="text-muted">
                                  {{ step.step_description }}
                                </div>
                                <div
                                  v-if="step.step_category"
                                  class="text-muted small"
                                >
                                  Category: {{ formatStepCategory(step.step_category) }}
                                </div>
                              </div>

                              <!-- Edit and Add buttons -->
                              <div class="btn-group btn-group-sm">
                                <!-- Edit button to modify step in library -->
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary"
                                  @click.stop="startEditStep(step)"
                                >
                                  Edit
                                </button>

                                <!-- Add button: shows "Added" and disables if already in template -->
                                <button
                                  type="button"
                                  class="btn"
                                  :class="isInTemplate(step) ? 'btn-secondary' : 'btn-primary'"
                                  :disabled="isInTemplate(step)"
                                  @click="addStep(step)"
                                >
                                  {{ isInTemplate(step) ? "Added" : "Add" }}
                                </button>
                              </div>
                            </div>

                            <!-- Edit mode view: shown only for the step being edited -->
                            <div v-else>
                              <!-- Editable step name field -->
                              <div class="mb-2">
                                <input
                                  v-model="editStepForm.step_name"
                                  type="text"
                                  class="form-control form-control-sm"
                                  placeholder="Step name"
                                />
                              </div>

                              <!-- Editable step description field -->
                              <div class="mb-2">
                                <textarea
                                  v-model="editStepForm.step_description"
                                  class="form-control form-control-sm"
                                  rows="2"
                                  placeholder="Step description"
                                ></textarea>
                              </div>

                              <!-- Editable category dropdown -->
                              <div class="mb-2">
                                <label class="form-label small mb-1">Category</label>
                                <select
                                  v-model="editStepForm.step_category"
                                  class="form-select form-select-sm"
                                >
                                  <option disabled value="">Select category...</option>
                                  <option
                                    v-for="opt in stepCategoryOptions"
                                    :key="opt.value"
                                    :value="opt.value"
                                  >
                                    {{ opt.label }}
                                  </option>
                                </select>
                              </div>

                              <!-- Cancel and Save buttons for edit mode -->
                              <div class="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary btn-sm"
                                  @click="cancelEditStep"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  class="btn btn-primary btn-sm"
                                  :disabled="
                                    savingStepEdit ||
                                    !editStepForm.step_name.trim() ||
                                    !editStepForm.step_category
                                  "
                                  @click="saveEditStep(step)"
                                >
                                  <!-- Show spinner while saving step edit -->
                                  <span
                                    v-if="savingStepEdit"
                                    class="spinner-border spinner-border-sm me-1"
                                  ></span>
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Right column: Ordered list of steps selected for this template -->
                    <div class="col-md-7">
                      <div class="border rounded p-3 h-100 bg-white">
                        <div class="d-flex justify-content-between mb-2">
                          <h2 class="h6 mb-0">Template Steps (in order)</h2>
                          <span class="help">
                            Use Up/Down to adjust the final order.
                          </span>
                        </div>

                        <!-- Empty state message when no steps have been selected -->
                        <div
                          v-if="selectedSteps.length === 0"
                          class="text-muted small"
                        >
                          No steps added yet. Select steps from the library on the left, or create a
                          new step.
                        </div>

                        <!-- Numbered list of steps in the template (in order) -->
                        <ol
                          v-else
                          class="list-group list-group-numbered small"
                        >
                          <li
                            v-for="(step, index) in selectedSteps"
                            :key="step.step_id || index"
                            class="list-group-item d-flex justify-content-between align-items-start"
                          >
                            <!-- Step name and description -->
                            <div class="me-3">
                              <div class="fw-semibold">
                                {{ step.step_name }}
                              </div>
                              <div class="text-muted">
                                {{ step.step_description }}
                              </div>
                            </div>

                            <!-- Buttons to reorder or remove step -->
                            <div class="btn-group btn-group-sm">
                              <!-- Move step up (disabled if first) -->
                              <button
                                type="button"
                                class="btn btn-outline-secondary"
                                :disabled="index === 0"
                                @click="moveStepUp(index)"
                              >
                                Up
                              </button>

                              <!-- Move step down (disabled if last) -->
                              <button
                                type="button"
                                class="btn btn-outline-secondary"
                                :disabled="index === selectedSteps.length - 1"
                                @click="moveStepDown(index)"
                              >
                                Down
                              </button>

                              <!-- Remove step from template -->
                              <button
                                type="button"
                                class="btn btn-outline-danger"
                                @click="removeStep(index)"
                              >
                                Remove
                              </button>
                            </div>
                          </li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <!-- Error message display (shown if any error occurs) -->
                  <div v-if="error" class="alert alert-danger py-2 mt-3">
                    {{ error }}
                  </div>

                  <!-- Form action buttons: Cancel and Save/Create -->
                  <div class="d-flex gap-2 mt-4">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="handleCancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      :disabled="saving"
                    >
                      <!-- Show spinner while saving template -->
                      <span
                        v-if="saving"
                        class="spinner-border spinner-border-sm me-1"
                      ></span>
                      {{ isEdit ? "Save Changes" : "Create Template" }}
                    </button>
                  </div>
                </form>
              </div>

            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
  name: "CreateTemplate",
  data() {
    return {
      // ===== Template metadata =====
      form: {
        template_name: "",
        template_description: "",
        active_template: true,
      },

      // ===== Steps management =====
      steps: [],          // Step library loaded from API
      selectedSteps: [],  // Steps selected for this template (in order)
      stepSearch: "",     // Search term for filtering steps in library

      // ===== New step creation form =====
      newStep: {
        step_name: "",
        step_description: "",
        step_category: "",
      },

      // ===== Step editing state =====
      editingStepId: null, // step_id of the step currently being edited
      savingStepEdit: false, // Loading state while saving step edit
      editStepForm: {
        step_name: "",
        step_description: "",
        step_category: "",
      },

      // ===== Step categories dropdown options =====
      stepCategoryOptions: [
        { value: "CLEANING",     label: "Cleaning" },
        { value: "REPAIR",       label: "Repair" },
        { value: "TESTING",      label: "Testing" },
        { value: "INSTALLATION", label: "Installation" },
      ],

      // ===== Loading and error states =====
      creatingStep: false, // Loading state for step creation
      loading: false,      // Loading state for initial data fetch
      saving: false,       // Loading state for saving template
      error: null,         // Error message to display

      // ===== API service instance =====
      api: new APIService(),
    };
  },

  computed: {
    /**
     * Returns true if we're in edit mode (template ID in route params)
     * vs create mode
     */
    isEdit() {
      return !!this.$route.params.id;
    },

    /**
     * Returns filtered list of steps based on search term
     * Searches in step_name and step_description
     */
    filteredSteps() {
      const list = Array.isArray(this.steps) ? this.steps : [];
      const term = this.stepSearch.trim().toLowerCase();
      
      if (!term) return list;
      
      return list.filter((s) => {
        return (
          (s.step_name || "").toLowerCase().includes(term) ||
          (s.step_description || "").toLowerCase().includes(term)
        );
      });
    },
  },

  created() {
    // Fetch initial data (steps library and template if editing)
    this.fetchInitialData();
  },

  methods: {
    /**
     * Loads the step library from API and template data if editing
     */
    async fetchInitialData() {
      this.loading = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");

        // Load step library from API
        const stepsRes = await this.api.getSteps(accessToken);
        this.steps = Array.isArray(stepsRes.data) ? stepsRes.data : [];

        // If editing an existing template, load its data
        if (this.isEdit) {
          const id = this.$route.params.id;
          const tmplRes = await this.api.getTemplate(id, accessToken);
          this.hydrateFromTemplate(tmplRes.data);
        }
      } catch (err) {
        console.error("Template editor load failed:", err.response?.data || err);
        this.error = "Unable to load template editor.";
      } finally {
        this.loading = false;
      }
    },

    /**
     * Populates form fields and selectedSteps from loaded template data
     * Handles both step_details and steps arrays for flexibility
     */
    hydrateFromTemplate(template) {
      if (!template) return;

      // Set template metadata
      this.form.template_name = template.template_name || "";
      this.form.template_description = template.template_description || "";
      this.form.active_template =
        typeof template.active_template === "boolean"
          ? template.active_template
          : true;

      // Extract steps from template (supports both field names)
      const rawSteps =
        Array.isArray(template.step_details)
          ? template.step_details
          : Array.isArray(template.steps)
          ? template.steps
          : [];

      if (!rawSteps.length) {
        this.selectedSteps = [];
        return;
      }

      // Create map for quick step lookup by ID
      const byId = new Map((this.steps || []).map((s) => [s.step_id, s]));

      // Sort steps by position/order field
      const ordered = [...rawSteps].sort((a, b) => {
        const ao = a.step_position ?? a.step_order ?? 0;
        const bo = b.step_position ?? b.step_order ?? 0;
        return ao - bo;
      });

      // Build selectedSteps array with all required fields filled in
      this.selectedSteps = ordered.map((ts, index) => {
        const lib = byId.get(ts.step_id) || {};
        return {
          step_id: ts.step_id,
          step_name: ts.step_name || lib.step_name || `Step ${index + 1}`,
          step_description:
            ts.step_description || lib.step_description || "",
          step_category:
            ts.step_category ?? lib.step_category ?? null,
        };
      });
    },

    /**
     * Returns true if the given step is already in the template
     */
    isInTemplate(step) {
      if (!step || !step.step_id) return false;
      const list = Array.isArray(this.selectedSteps)
        ? this.selectedSteps
        : [];
      return list.some((s) => s.step_id === step.step_id);
    },

    /**
     * Adds a step from the library to the template's selectedSteps
     */
    addStep(step) {
      if (!step || !step.step_id) return;
      if (this.isInTemplate(step)) return;

      const list = Array.isArray(this.selectedSteps)
        ? this.selectedSteps
        : [];
      
      list.push({
        step_id: step.step_id,
        step_name: step.step_name,
        step_description: step.step_description,
        step_category: step.step_category,
      });
      
      this.selectedSteps = list;
    },

    /**
     * Moves a step up one position in the template order
     */
    moveStepUp(index) {
      if (index <= 0) return;
      if (!Array.isArray(this.selectedSteps)) return;
      
      const arr = this.selectedSteps;
      const tmp = arr[index - 1];

      if (this.$set) {
        this.$set(this.selectedSteps, index - 1, arr[index]);
        this.$set(this.selectedSteps, index, tmp);
      } else {
        [this.selectedSteps[index - 1], this.selectedSteps[index]] = [
          this.selectedSteps[index],
          tmp,
        ];
      }
    },

    /**
     * Moves a step down one position in the template order
     */
    moveStepDown(index) {
      if (!Array.isArray(this.selectedSteps)) return;
      if (index >= this.selectedSteps.length - 1) return;
      
      const arr = this.selectedSteps;
      const tmp = arr[index + 1];

      if (this.$set) {
        this.$set(this.selectedSteps, index + 1, arr[index]);
        this.$set(this.selectedSteps, index, tmp);
      } else {
        [this.selectedSteps[index + 1], this.selectedSteps[index]] = [
          this.selectedSteps[index],
          tmp,
        ];
      }
    },

    /**
     * Removes a step from the template
     */
    removeStep(index) {
      if (!Array.isArray(this.selectedSteps)) return;
      this.selectedSteps.splice(index, 1);
    },

    /**
     * Creates a new step via API, adds it to library and template
     */
    async handleCreateStep() {
      this.error = null;
      
      if (!this.newStep.step_name.trim()) {
        this.error = "Step name is required.";
        return;
      }

      this.creatingStep = true;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const payload = {
          step_name: this.newStep.step_name,
          step_description: this.newStep.step_description,
          step_category: this.newStep.step_category,
        };
        
        // Call API to create step
        const res = await this.api.createStep(payload, accessToken);
        const created = res.data;

        if (!created || !created.step_id) {
          throw new Error("Backend did not return a step_id");
        }

        // Add new step to library
        if (!Array.isArray(this.steps)) {
          this.steps = [];
        }
        this.steps.push(created);

        // Add new step to selected steps for this template
        if (!Array.isArray(this.selectedSteps)) {
          this.selectedSteps = [];
        }
        this.selectedSteps.push({
          step_id: created.step_id,
          step_name: created.step_name,
          step_description: created.step_description,
          step_category: created.step_category,
        });

        // Reset new step form for next creation
        this.newStep.step_name = "";
        this.newStep.step_description = "";
        this.newStep.step_category = "";
      } catch (err) {
        console.error("Create step failed:", err.response?.data || err);
        this.error = this.parseErrorResponse(err, "Unable to create step.");
      } finally {
        this.creatingStep = false;
      }
    },

    /**
     * Converts step category value to display label
     */
    formatStepCategory(value) {
      const opt = this.stepCategoryOptions.find((o) => o.value === value);
      return opt ? opt.label : value || "Uncategorized";
    },

    /**
     * Enters edit mode for a step in the library
     */
    startEditStep(step) {
      if (!step || !step.step_id) return;
      
      this.error = null;
      this.editingStepId = step.step_id;
      this.editStepForm = {
        step_name: step.step_name || "",
        step_description: step.step_description || "",
        step_category: step.step_category || "",
      };
    },

    /**
     * Cancels step editing and resets the form
     */
    cancelEditStep() {
      this.editingStepId = null;
      this.savingStepEdit = false;
      this.editStepForm = {
        step_name: "",
        step_description: "",
        step_category: "",
      };
    },

    /**
     * Saves changes to a step via API and updates library and template
     */
    async saveEditStep(step) {
      if (!step || !step.step_id) return;

      this.error = null;
      
      // Validate required fields
      if (!this.editStepForm.step_name.trim()) {
        this.error = "Step name is required.";
        return;
      }
      if (!this.editStepForm.step_category) {
        this.error = "Step category is required.";
        return;
      }

      this.savingStepEdit = true;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const payload = {
          step_name: this.editStepForm.step_name,
          step_description: this.editStepForm.step_description,
          step_category: this.editStepForm.step_category,
        };

        // Call API to update step
        const res = await this.api.updateStep(step.step_id, payload, accessToken);
        const updated = res.data || { ...step, ...payload };

        // Update step in library
        if (Array.isArray(this.steps)) {
          const idx = this.steps.findIndex((s) => s.step_id === step.step_id);
          if (idx !== -1) {
            this.steps[idx] = {
              ...this.steps[idx],
              ...updated,
            };
          }
        }

        // Update any instances of this step in selectedSteps
        if (Array.isArray(this.selectedSteps)) {
          this.selectedSteps = this.selectedSteps.map((s) => {
            if (s.step_id !== step.step_id) return s;
            return {
              ...s,
              step_name: updated.step_name,
              step_description: updated.step_description,
              step_category: updated.step_category,
            };
          });
        }

        // Exit edit mode
        this.cancelEditStep();
      } catch (err) {
        console.error("Update step failed:", err.response?.data || err);
        this.error = this.parseErrorResponse(err, "Unable to update step.");
      } finally {
        this.savingStepEdit = false;
      }
    },

    /**
     * Saves the template (create new or update existing)
     * Validates form and sends payload to API
     */
    async handleSubmit() {
      this.error = null;

      // Validate template name
      if (!this.form.template_name.trim()) {
        this.error = "Template name is required.";
        return;
      }

      // Validate at least one step is selected
      if (!Array.isArray(this.selectedSteps) || this.selectedSteps.length === 0) {
        this.error = "Add at least one step to the template.";
        return;
      }

      this.saving = true;
      try {
        const accessToken = localStorage.getItem("accessToken");

        // Build API payload with template metadata and steps with positions
        const payload = {
          template_name: this.form.template_name,
          template_description: this.form.template_description,
          active_template: this.form.active_template,
          steps: this.selectedSteps.map((s, index) => ({
            step_id: s.step_id,
            step_position: index + 1,
          })),
        };

        // Create or update template
        if (this.isEdit) {
          const id = this.$route.params.id;
          await this.api.updateTemplate(id, payload, accessToken);
        } else {
          await this.api.createTemplate(payload, accessToken);
        }

        // Redirect to template list on success
        this.$router.push({ name: "ViewTemplates" });
      } catch (err) {
        console.error("Template save failed:", err.response?.data || err);
        this.error = this.parseErrorResponse(err, "Unable to save template.");
      } finally {
        this.saving = false;
      }
    },

    /**
     * Navigates away from the form without saving
     * Goes to template detail page if editing, or to template list if creating
     */
    handleCancel() {
      if (this.isEdit && this.$route.params.id) {
        this.$router.push({
          name: "TemplateDetail",
          params: { id: this.$route.params.id },
        });
      } else {
        this.$router.push({ name: "ViewTemplates" });
      }
    },

    /**
     * Parses API error responses into user-friendly messages
     * Handles common backend error formats
     */
    parseErrorResponse(err, fallbackMessage) {
      if (!err.response || !err.response.data) {
        return fallbackMessage;
      }

      const data = err.response.data;

      // Handle string error messages
      if (typeof data === "string") {
        return data;
      }

      // Handle { detail: "message" } format
      if (data.detail) {
        return data.detail;
      }

      // Handle { error: "message" } format
      if (data.error) {
        return data.error;
      }

      // Handle { field_name: ["error1", "error2"] } format (Django validation)
      if (typeof data === "object") {
        const messages = [];
        for (const [field, errors] of Object.entries(data)) {
          if (Array.isArray(errors)) {
            messages.push(`${field}: ${errors.join(", ")}`);
          } else if (typeof errors === "string") {
            messages.push(`${field}: ${errors}`);
          }
        }
        if (messages.length > 0) {
          return messages.join(". ");
        }
      }

      return fallbackMessage;
    },
  },
};
</script>